/** @jsx jsx */

import React, { useState } from "react";
import { Typography, Input, InputNumber, message } from "antd";
import { jsx } from "@emotion/core";

import CustomButton from "../button";

import {
  otpContents as CONSTANTS,
  maxAttempts,
  otpLength,
  StatusTypes
} from "./OTPConstants";
import { MOBILE_COUNTRY_CODE } from "../../Constants";
import { FormInput } from "../../components/formHelpers";
import useAPI from "../../helperHooks/useAPI";
import { OtpService } from "../../services/otpService/index";

interface Props {
  handleChange: (e: React.ChangeEvent<any>) => void;
  phoneNumber: string;
  //check phoneNumber is valid
  isValid: boolean;
  register: boolean;
  className?: string;
  label?: string;
  onGenerateSuccess?: (termsAndCondUrl?: string) => void;
  onVerifySuccess?: (phoneNumber: string) => void;
  //function to reset otp component
  onReset?: () => void;
  placeholder?: string;
}

const { Text } = Typography;

const OTP: React.FC<Props> = (props: Props) => {
  const {
    register,
    className,
    label,
    placeholder,
    handleChange,
    phoneNumber,
    onVerifySuccess,
    onGenerateSuccess,
    isValid,
    onReset
  } = props;

  const { callAPI: generateOtp, ...generateOtpData } = useAPI(
    OtpService.generateOtp
  );

  const { callAPI: verifyOtp, ...verifyOtpData } = useAPI(OtpService.verifyOtp);
  //state to track otp
  const [OTPStatus, setOTPStatus] = useState<StatusTypes>(StatusTypes.new);

  const { loading: isGenerating, error: generateError } = generateOtpData;

  const { loading: isVerifying, error: verifyError, response } = verifyOtpData;

  //check otp status
  const otpNew = OTPStatus === StatusTypes.new;
  const otpVerified = OTPStatus === StatusTypes.verified;

  const loading = isGenerating || isVerifying;
  const exceedOtpAttempts = response?.otpAttempts || 0;
  //disable generate button while loading or it exceeds otp attempts
  const generateDisable = loading || exceedOtpAttempts > maxAttempts;

  const otpError = verifyError || generateError;
  const [otp, setOtp] = useState("");
  //Decreases cyclomatic complexity
  const showOtp = () => {
    return !isValid && phoneNumber && !otpVerified;
  };
  const verifyDisable = loading || otp.length !== otpLength || otpNew;
  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value }
    } = e;
    if (typeof value === "string" && !Number.isNaN(Number(value))) {
      setOtp(value);
    }
  };
  const generateButtonText = !otpNew
    ? CONSTANTS.buttons.resendOtp
    : CONSTANTS.buttons.generateOtp;
  const generateOtpFunction = () => {
    generateOtp(
      {
        phoneNumber,
        register
      },
      generateResponse => {
        message.success({
          content: register
            ? CONSTANTS.feedbacks.registerOtpSuccess
            : CONSTANTS.feedbacks.loginOtpSuccess
        });
        setOTPStatus(StatusTypes.generated);
        if (onGenerateSuccess) {
          onGenerateSuccess(generateResponse?.termsAndCondUrl);
        }
      }
    );
  };
  const verifyOtpFunction = () =>
    verifyOtp({ phoneNumber, otp: Number(otp) }, () => {
      message.success({ content: CONSTANTS.feedbacks.verifySuccess });
      setOTPStatus(StatusTypes.verified);
      if (onVerifySuccess) {
        onVerifySuccess(phoneNumber);
      }
    });

  const renderForm = (
    <div className={className || ""}>
      <FormInput
        autoFocus
        name="phoneNumber"
        label={label}
        placeholder={placeholder}
        onChange={e => {
          handleChange(e);
          setOTPStatus(StatusTypes.new);
          setOtp("");
        }}
        disabled={otpVerified}
        onClear={() => {
          //reset otp status along with reset function
          setOTPStatus(StatusTypes.new);
          if (onReset) {
            onReset();
          }
        }}
        showClear={otpVerified}
        mandatory
      >
        <InputNumber
          className="mr-3 country-code"
          value={MOBILE_COUNTRY_CODE}
          formatter={value => `+${value}`}
          disabled
        />
      </FormInput>
      {showOtp() && (
        <React.Fragment>
          <CustomButton
            type="primary"
            size="default"
            containerDivClassName="justify-content-center mt-4"
            onClick={generateOtpFunction}
            disabled={generateDisable}
            loading={isGenerating}
          >
            {generateButtonText}
          </CustomButton>
          <label className="d-block mt-3">{CONSTANTS.labels.otpField}</label>
          <section className="d-flex">
            <Input
              placeholder={CONSTANTS.placeholders.otp}
              name="otp"
              value={otp}
              maxLength={4}
              onChange={handleOnChange}
              disabled={otpNew}
            />
            <CustomButton
              containerDivClassName="ml-4"
              className="px-5"
              size="default"
              onClick={verifyOtpFunction}
              disabled={verifyDisable}
              loading={isVerifying}
            >
              {CONSTANTS.buttons.verifyOtp}
            </CustomButton>
          </section>
          {otpError && (
            <Text className="error d-block mt-3">
              {otpError.message || JSON.stringify(otpError)}
            </Text>
          )}
        </React.Fragment>
      )}
    </div>
  );
  return renderForm;
};

export default OTP;
